/**
 * `VapaaAjanTapaturmavakuutusRoute` — bespoke route for the
 * `/vapaa-ajan-tapaturmavakuutus` URL (TA20.4, Phase A PR C.1).
 *
 * Lift posture: prose-into-seed; widgets stay in page (per
 * `phase-a-content-inventory.md` §"Verified inventory"). The article's
 * static prose lifts into `body_html`; the status banner, vakuutusnumero
 * row, accordion sections, and footer outbound link stay as page-level
 * chrome around the body.
 *
 * Composition (amended 2026-05-11 per D-PD-003 — the per-article
 * demo-disclosure aside was removed during the proto-parity
 * convergence sweep; demo identity is now carried at the route boundary,
 * see `docs/governance/proto-divergences.md` D-PD-003):
 *
 *   1. H1 carrying `article.title`.
 *   2. `PolicyStatusCard` — a `<section aria-label={statusHeading}>`
 *      landmark whose body is rendered as `<p>` paragraphs (status
 *      heading + insurance-number sentence). NOT a `role="status"` /
 *      `aria-live="polite"` region because the values do not change at
 *      runtime in Phase A; AT users land on the landmark via normal
 *      reading order. The status heading text comes from
 *      `metadata.statusBannerFi`; the insurance number comes from
 *      `metadata.insuranceNumber` and is composed into a single
 *      sentence inside the card (no `<dl>`/`<dt>`/`<dd>` semantics —
 *      the visual reads as a single sentence, not a term/definition
 *      list). The shared chrome lives in
 *      `content/routes/PolicyStatusCard.tsx`.
 *   3. Body via `SanitizedHtml` (FR-049).
 *   4. Accordion: native HTML5 `<details><summary>` for each section in
 *      `metadata.accordionSections`. Each section's body renders through
 *      `SanitizedHtml` (the prose may carry inline markup).
 *   5. Footer outbound link — `<a target="_blank"
 *      rel="noopener noreferrer">` with `metadata.outboundLink`.
 *
 * State matrix mirrors `ArticleDetailPage`. Metadata narrowing uses a
 * type guard against `VapaaAjanTapaturmaMetadata`; a shape violation
 * throws so the caller-mounted `PortErrorBoundary` surfaces the
 * diagnostic.
 */

import type {
  AccordionSection,
  VapaaAjanTapaturmaMetadata,
} from '@omaplus/shared-types/content-metadata';
import type { JSX } from 'react';
import { AnalyticsRouteReady } from '../../analytics/AnalyticsCaptureProvider.js';
import { NotFound } from '../../routes/NotFound.js';
import { SanitizedHtml } from '../components/SanitizedHtml.js';
import { useArticle } from '../hooks/useArticle.js';
import { ArticleContentBlocks } from './ArticleContentBlocks.js';
import {
  bodyClasses,
  DetailSurfaceShell,
  DetailSurfaceSkeleton,
  headingClasses,
  PolicyStatusCard,
} from './articleDetailSurface.js';
import { isOutboundLink } from './contentMetadataGuards.js';
import { DetailSurfaceHero } from './detailSurfaceHero.js';

const ARTICLE_SLUG = 'vapaa-ajan-tapaturmavakuutus';

const accordionListClasses = 'flex flex-col gap-2';
const accordionSummaryClasses = 'cursor-pointer select-none text-base font-medium text-foreground';
const accordionBodyClasses = 'flex flex-col gap-2 pt-2 text-sm text-foreground';
const outboundLinkClasses = 'self-start text-sm font-medium text-primary underline';

/**
 * Type guard narrowing `Article.metadata` to
 * `VapaaAjanTapaturmaMetadata`. Walks the four required fields plus the
 * inner `accordionSections` array. The CMS may remove the optional footer
 * link, so its presence is validated only when provided.
 */
function isAccordionSection(value: unknown): value is AccordionSection {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return typeof record['titleFi'] === 'string' && typeof record['bodyHtml'] === 'string';
}

function isVapaaAjanTapaturmaMetadata(value: unknown): value is VapaaAjanTapaturmaMetadata {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (
    typeof record['insuranceNumber'] !== 'string' ||
    typeof record['statusBannerFi'] !== 'string'
  ) {
    return false;
  }
  if (!Array.isArray(record['accordionSections'])) {
    return false;
  }
  for (const section of record['accordionSections']) {
    if (!isAccordionSection(section)) {
      return false;
    }
  }
  return record['outboundLink'] === undefined || isOutboundLink(record['outboundLink']);
}

export function VapaaAjanTapaturmavakuutusRoute(): JSX.Element {
  const query = useArticle(ARTICLE_SLUG);

  if (query.isPending) {
    return <DetailSurfaceSkeleton testId="vapaa-ajan-tapaturma-route-skeleton" />;
  }

  if (query.isError) {
    throw (
      query.error ?? new Error('useArticle resolved to an error state without an Error instance')
    );
  }

  if (query.data === null) {
    return <NotFound />;
  }

  const article = query.data;
  const hasContentBlocks = article.contentBlocks !== undefined;
  if (hasContentBlocks) {
    return (
      <DetailSurfaceShell as="article">
        <AnalyticsRouteReady
          pageKey={{ kind: 'memberRoute', route: 'leisure-accident-insurance' }}
        />
        <DetailSurfaceHero
          imageUrl={article.imageUrl}
          mediaLabel={article.title}
          imageAlt={article.imageAlt}
        />
        <h1 className={headingClasses}>{article.title}</h1>
        <ArticleContentBlocks contentBlocks={article.contentBlocks} />
      </DetailSurfaceShell>
    );
  }
  if (!isVapaaAjanTapaturmaMetadata(article.metadata)) {
    throw new Error(
      `VapaaAjanTapaturmavakuutusRoute: article "${ARTICLE_SLUG}" metadata does not match VapaaAjanTapaturmaMetadata shape`,
    );
  }
  const metadata = article.metadata;

  return (
    <DetailSurfaceShell as="article">
      <AnalyticsRouteReady pageKey={{ kind: 'memberRoute', route: 'leisure-accident-insurance' }} />
      <DetailSurfaceHero
        imageUrl={article.imageUrl}
        mediaLabel={article.title}
        imageAlt={article.imageAlt}
      />
      <h1 className={headingClasses}>{article.title}</h1>
      <PolicyStatusCard
        statusHeading={metadata.statusBannerFi}
        insuranceNumber={metadata.insuranceNumber}
        insuranceNumberLabel={metadata.insuranceNumberLabelFi}
      />
      <SanitizedHtml as="div" className={bodyClasses} html={article.bodyHtml} />
      <section className={accordionListClasses} aria-label="Vakuutusehdot">
        {metadata.accordionSections.map((section) => (
          <details key={`${section.titleFi}-${section.bodyHtml}`}>
            <summary className={accordionSummaryClasses}>{section.titleFi}</summary>
            <SanitizedHtml as="div" className={accordionBodyClasses} html={section.bodyHtml} />
          </details>
        ))}
      </section>
      {metadata.outboundLink !== undefined ? (
        <a
          className={outboundLinkClasses}
          href={metadata.outboundLink.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {metadata.outboundLink.labelFi}
        </a>
      ) : null}
    </DetailSurfaceShell>
  );
}
